import { detectVideoProvider } from './videoProvider';

/**
 * Shared helpers for the editable media gallery used by the News forms
 * (admin + member). This is a faithful port of the inline gallery logic in
 * AdminActivities so News behaves exactly like Activities, without touching the
 * Activity screen.
 *
 * Editable item shape (form state):
 *   { key, kind:'image'|'video'|'embed', url, embed_url, provider, alt_text,
 *     mediaId, error }
 */

let keySeq = 0;

/** Stable unique key for a gallery row while it lives in form state. */
export const nextGalleryKey = () => `g${Date.now()}-${keySeq++}`;

/**
 * Map a loaded record's `gallery` array (NewsResource / ActivityResource shape:
 * { id, media_id, type, url, embed_url, provider, alt_text, position }) into the
 * editable form-item shape above. List/detail items may already carry the
 * gallery; callers still re-fetch the full record on edit for safety.
 */
export function galleryItemsFromRecord(record) {
  return (record?.gallery || []).map((g) => {
    if (g.type === 'embed') {
      return {
        key: nextGalleryKey(),
        kind: 'embed',
        url: g.url,
        embed_url: g.embed_url,
        provider: g.provider,
        alt_text: g.alt_text || '',
        mediaId: null,
        error: '',
      };
    }
    return {
      key: nextGalleryKey(),
      kind: g.type === 'video' ? 'video' : 'image',
      url: g.url,
      embed_url: null,
      provider: null,
      alt_text: g.alt_text || '',
      mediaId: g.media_id ?? null,
      error: '',
    };
  });
}

/**
 * Build the API `gallery` payload: `{ media_id }` for uploads, `{ url }` for
 * embeds. Array order is the display order. Throws if an upload item lost its
 * media reference so the caller can surface a clear message instead of a 422.
 */
export function buildGalleryPayload(items) {
  return items.map((g, i) => {
    if (g.kind === 'embed') return { url: g.url };
    if (g.mediaId == null) {
      throw new Error(
        `Gallery item ${i + 1} is missing its media reference. Remove and re-add it, then save.`
      );
    }
    return { media_id: g.mediaId };
  });
}

/**
 * Map a Laravel 422 `gallery.*` error bag onto items by index. Returns a fresh
 * items array (errors cleared then re-applied) plus whether anything matched, so
 * the caller can decide between the field-error and gallery-error toast.
 */
export function applyGalleryErrors(items, errors) {
  const next = items.map((g) => ({ ...g, error: '' }));
  let matched = false;
  if (errors) {
    Object.entries(errors).forEach(([key, msgs]) => {
      const m = /^gallery\.(\d+)/.exec(key);
      if (m) {
        const idx = Number(m[1]);
        if (next[idx]) {
          next[idx].error = Array.isArray(msgs) ? msgs[0] : String(msgs);
          matched = true;
        }
      }
    });
  }
  return { items: next, matched };
}

export { detectVideoProvider };
